
import type { User } from './auth';

// ✅ ACTUALIZADA: Interface para miembros de la familia según nueva estructura
export interface FamilyMemberDto {
  id: number;
  relationshipType: 'SPOUSE' | 'CHILD' | 'OTHER';
  name: string;                        // antes: nombre
  age: number;                         // antes: edad
  gender: string;                      // antes: sexo
  paternal_lastname?: string;          // Apellido paterno
  maternal_lastname?: string;          // Apellido materno
  isPrimaryContact: boolean;
}

// ✅ Actualizado según GUIA_FRONTEND_REGISTRO_LLAMADAS_COMPLETA.md
// Interface para miembros de la familia en el formulario
export interface FamilyMemberFormData {
  id?: number;                        // ← Para UPDATE y DELETE (del backend)
  name: string;
  relationship: string;                // Código del catálogo FAMILY_RELATIONSHIP
  relationshipType?: 'SPOUSE' | 'CHILD' | 'OTHER'; // ← Para backend
  age: number;
  gender: string;
  paternal_lastname: string;          // Apellido paterno (en formulario)
  paternalLastName?: string;           // ← Para backend
  maternal_lastname: string;           // Apellido materno (en formulario)
  maternalLastName?: string;           // ← Para backend
  isPrimaryContact?: boolean;          // ← Para backend
  operation?: 'CREATE' | 'UPDATE' | 'DELETE'; // ← Operación solicitada
}

// ✅ MANTENER: Interface para hijos del empleado (compatibilidad)
export interface EmployeeChild {
  id?: number;
  nombre: string;
  edad: number;
  sexo: string;
}

export interface Assignment {
  id?: number;
  specialistId: number;
  specialistName?: string;
  assignedDate: string;
  status: AssignmentStatus;
  notes?: string;
}

// ✅ CAMBIO: De enum a objeto constante
export const AssignmentStatus = {
  PENDING: 'PENDING',
  ASSIGNED: 'ASSIGNED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED'
} as const;

export type AssignmentStatus = typeof AssignmentStatus[keyof typeof AssignmentStatus];

// ✅ CAMBIO: De enum a objeto constante
export const PhoneCallStatus = {
  DRAFT: 'DRAFT',
  CAPTURED: 'CAPTURED',
  ASSIGNED: 'ASSIGNED',
  IN_PROGRESS: 'IN_PROGRESS',
  EVIDENCE_PENDING: 'EVIDENCE_PENDING',
  COMPLETED: 'COMPLETED'
} as const;

export type PhoneCallStatus = typeof PhoneCallStatus[keyof typeof PhoneCallStatus];

/** Valores de status aceptados por el endpoint GET /phone-calls/agent (FRONTEND-FILTROS-LLAMADAS-TELEFONICAS.md) */
export const PHONE_CALL_AGENT_STATUS_VALUES = [
  'DRAFT',
  'CAPTURED',
  'ASSIGNED',
  'IN_PROGRESS',
  'EVIDENCE_PENDING',
  'COMPLETED'
] as const;

export type PhoneCallAgentStatus = typeof PHONE_CALL_AGENT_STATUS_VALUES[number];

/** Valores de scope aceptados por GET /phone-calls/agent (guia-front-seguimiento-llamadas.md) */
export const PHONE_CALL_AGENT_SCOPE_VALUES = ['created', 'assigned', 'all'] as const;
export type PhoneCallAgentScope = typeof PHONE_CALL_AGENT_SCOPE_VALUES[number];

/** Filtros del endpoint GET /phone-calls/agent según FRONTEND-FILTROS-LLAMADAS-TELEFONICAS.md (búsqueda por texto, no por ID) */
export interface PhoneCallAgentFilters {
  employeeName?: string;
  patientName?: string;
  companyName?: string;
  status?: PhoneCallAgentStatus | string;
  dateFrom?: string; // yyyy-MM-dd
  dateTo?: string;   // yyyy-MM-dd
  /** created = creadas por mí; assigned = para mi seguimiento; all = unión */
  scope?: PhoneCallAgentScope;
}

// ✅ ACTUALIZADA: Interface para Employee según nueva estructura del backend
export interface Employee {
  id?: number;
  email: string;
  firstName: string;                   // antes: nombre
  lastName: string;                    // antes: apellidoPaterno
  middleName: string;                  // antes: apellidoMaterno
  age: number;                         // antes: edad
  gender: string;                      // antes: sexo
  city: string;                        // antes: ciudad
  position: string;                    // antes: puesto
  accountId: number;                   // antes: empresaId
  accountOrgPath?: string;             // ✅ NUEVO: Ruta completa de la empresa
  lengthOfService: string;             // Tiempo de Servicio
  maritalStatus: string;               // antes: estadoCivil
  numberOfChildren: number;            // antes: numeroHijos
  familyMembers: FamilyMemberDto[];    // antes: children
  
  // Campos de pareja
  partnerName?: string;
  partnerAge?: number;
  partnerGender?: string;
  
  // Método helper
  getFullName(): string;
}

// ✅ NUEVA: Interface para ClientDto según nueva estructura del backend
export interface ClientDto {
  id: number;
  accountId: number;
  externalClientId?: number;
  firstName: string;                   // Para API de ComPsych
  lastName: string;                    // Para API de ComPsych
  
  // Datos personales internos (NUEVOS)
  privateFirstName?: string;           // Uso privado interno
  privateLastName?: string;            // Uso privado interno
  privateMiddleName?: string;          // Uso privado interno
  
  gender?: string;
  age?: number;
  employeeRelationship?: string;
  employeeRelationshipDescription?: string;
  countryAbbreviation?: string;
  lengthOfService?: string;
  ageRange?: string;
  email?: string;
  privatePreviousPsychologicalTreatment?: boolean;
  
  // Asignación de especialista (NUEVOS)
  assignedSpecialistId?: number;
  assignedSpecialistName?: string;
  assignmentDate?: string;
  assignmentNotes?: string;
  assignmentStatus?: string;
  
  // Métodos helper
  getFullName(): string;               // Usa datos privados si están disponibles
  getPrivateFullName(): string;        // Solo datos privados
}

// ✅ ACTUALIZADA: Interface principal para PhoneCall según nueva estructura del backend
export interface PhoneCall {
  id?: number;
  
  // Referencia al empleado
  employeeId: number;
  employee?: Employee;
  
  // Referencia al paciente (NUEVO)
  patientId?: number | null;
  patient?: ClientDto | null;

  /** Listado de clientes/beneficiarios con referrals (p. ej. GET listado agente o detalle). */
  patients?: Array<Record<string, unknown>> | null;

  // Call control data (RENOMBRADOS)
  schedule: string;                    // antes: horario
  duration: string;                    // antes: duracion
  callDate: string;                    // antes: fecha (ISO 8601)
  
  // Contact information (RENOMBRADOS)
  homePhone: string;                   // antes: telefonoCasa
  mobilePhone: string;                 // antes: telefonoCelular
  
  // Service types (eliminados - ahora se usa 'product')
  
  // History (RENOMBRADO)
  previousPsychologicalTreatment: boolean; // antes: tratamientoPsicologicoAnterior
  
  // Additional information (RENOMBRADO)
  observations: string;                // antes: observaciones
  
  // Status
  status: PhoneCallStatus;
  
  // Privacy notice
  privacyConsent: boolean;
  privacyConsentDate?: string;
  privacyConsentIp?: string;
  privacyVersion: string;

  /** Semáforo de severidad de la llamada (independiente de riskType del referral). */
  severityLevel?: 'GREEN' | 'YELLOW' | 'RED' | null;

  /** Texto libre: cómo se enteró del programa (nivel llamada, no por servicio). */
  referralSourceIntake?: string | null;

  /** Notas capturadas durante atención de crisis telefónica. */
  crisisNotes?: string | null;

  /** Marca explícita de si la llamada registró un evento de crisis. */
  hadCrisis?: boolean;
  
  // Metadatos
  createdBy?: number | Pick<User, 'id' | 'username' | 'fullName' | 'email'>;
  createdAt?: string;
  updatedAt?: string;
  
  // ✅ NUEVO: Usuario de seguimiento
  assignedTo?: Pick<User, 'id' | 'username' | 'fullName' | 'email'> | null;
  assignedToDate?: string | null;
}

// ✅ ACTUALIZADA: Interface para datos del empleado en el formulario según nueva estructura
export interface EmployeeFormData {
  cveEnt?: string | null;
  cveMun?: string | null;
  cveLoc?: string | null;
  email: string;
  firstName: string;                   // antes: nombre
  lastName: string;                    // antes: apellidoPaterno
  middleName: string;                  // antes: apellidoMaterno
  age: number;                         // antes: edad
  gender: string;                      // antes: sexo
  
  // Datos laborales
  accountId: number;                   // antes: empresaId
  city: string;                        // antes: ciudad
  position: string;                    // antes: puesto
  lengthOfService: string;             // Tiempo de Servicio
  
  // Información familiar
  maritalStatus: string;               // antes: estadoCivil
  numberOfChildren: number;            // antes: numeroHijos
  familyMembers: FamilyMemberFormData[];    // antes: children
  
  // Contacto del empleado (usado cuando el paciente es menor de edad)
  mobilePhone?: string;
  homePhone?: string;

  // Campos de pareja
  partnerName?: string;
  partnerAge?: number;
  partnerGender?: string;
}

// ✅ Actualizado según GUIA_FRONTEND_REGISTRO_LLAMADAS_COMPLETA.md
// Interface para formulario según nueva estructura del backend
export interface PhoneCallFormData {
  // Aviso de Privacidad
  privacyConsent: boolean;
  privacyVersion: string;
  
  // Información de Control (RENOMBRADOS)
  schedule: string;                    // antes: horario
  duration: string;                    // antes: duracion
  
  // ✅ NUEVO: employeeId cuando se selecciona un empleado existente
  employeeId?: number | null;
  
  // Datos del empleado (objeto separado)
  employee: EmployeeFormData;
  
  // Datos del paciente (NUEVO - similar a CaptureModal)
  patient: {
    relationship: string;               // Relación con empleado (Employee, Partner, Child, Other)
    firstName: string;                  // Nombre
    lastName: string;                   // Apellido
    gender: string;                     // Género
    ageRange: string;                   // Rango de Edad
    email: string;                      // Email
  };
  
  // Referencia al paciente (NUEVO)
  patientId?: number | null;
  
  // ✅ NUEVO: Referencia al referral (para actualización)
  referralId?: number | null;
  
  // Contact information (RENOMBRADOS)
  homePhone: string;                   // antes: telefonoCasa
  mobilePhone: string;                 // antes: telefonoCelular
  
  // Service types (eliminados - ahora se usa 'product')
  
  // History (RENOMBRADO)
  previousPsychologicalTreatment: boolean; // antes: tratamientoPsicologicoAnterior
  
  // Additional information (RENOMBRADO)
  observations: string;                // antes: observaciones
  
  // Referral information (NUEVO - para guardar entidad Referral)
  product: string;                     // Producto
  callDate: Date;                     // Fecha de la llamada
  presentingIssue: string;            // Problema Presentado
  riskType: string;                   // Tipo de Riesgo
  employeeType: string;               // Tipo de empleado
  referralSource: string;             // Origen
  deliveryMethod: string;             // Método de Entrega
  specialIncident?: string;           // Incidente Especial (opcional)
  subCategory?: string | null;        // ✅ NUEVO: Subcategoría (requerida para LegalConnect, FinancialConnect, etc.)
  locationNumber?: string;             // ✅ NUEVO: Location Number (automático según producto)
  
  // ✅ NUEVO: Usuario de seguimiento
  assignedToUserId?: number | null;
}

// ✅ ACTUALIZADA: Interface para datos que se envían al backend según nueva estructura
export interface PhoneCallCreateRequest {
  // Referencia al empleado
  employeeId: number;
  
  // Referencia al paciente (NUEVO)
  patientId?: number | null;
  
  // Call control data (RENOMBRADOS)
  schedule: string;                    // antes: horario
  duration: string;                    // antes: duracion
  callDate: string;                    // antes: fecha (ISO 8601)
  
  // Contact information (RENOMBRADOS)
  homePhone: string;                   // antes: telefonoCasa
  mobilePhone: string;                 // antes: telefonoCelular
  
  // Service types (eliminados - ahora se usa 'product')
  
  // History (RENOMBRADO)
  previousPsychologicalTreatment: boolean; // antes: tratamientoPsicologicoAnterior
  
  // Additional information (RENOMBRADO)
  observations: string;                // antes: observaciones
  
  // Privacy notice
  privacyConsent: boolean;
  privacyConsentDate?: string;
  privacyConsentIp?: string;
  privacyVersion: string;
  
  // ✅ NUEVO: Usuario de seguimiento (obligatorio en creación)
  assignedToUserId: number;
}

// ✅ NUEVO: Interface para contactos de especialistas
export interface CoverageArea {
  coverageAreaId: number;
  code: string;
  description: string;
  isActive: boolean;
}

export interface SpecialistContact {
  contactId?: number;
  specialistId?: number;
  contactType: string;    // "EMAIL", "PHONE_OFFICE", "PHONE_MOBILE", "WHATSAPP", etc.
  contactValue: string;    // "dr@example.com", "555-1234"
  isPrimary: boolean;      // true/false
  notes?: string;          // "Teléfono de emergencia"
  isActive?: boolean;      // true/false
  createdAt?: string;
}

// ✅ NUEVO: Interface para direcciones temporales según ESPECIFICACION_FRONTEND_DIRECCIONES_TEMPORALES.md
export interface TemporaryAddressDTO {
  id: number;              // ID único de la dirección temporal
  direccion: string;       // Dirección completa en texto libre (máx. 1000 caracteres)
  horario?: string;        // Horario de atención (máx. 500 caracteres, opcional)
  notas?: string;         // Notas adicionales (máx. 500 caracteres, opcional)
}

// ✅ ACTUALIZADA: Interface para especialistas según nueva API refactorizada
export interface Specialist {
  specialistId: number;
  fullName: string;
  productArea: string; // ✅ CAMBIADO: usar productArea según especificación
  professionalLicense?: string;
  notes?: string;
  active: boolean; // ✅ CORREGIDO: El backend usa 'active' no 'isActive'
  /** Alias / campo de estatus según guía de status (puede venir junto a `active`). */
  isActive?: boolean;
  statusChangeReason?: string | null;
  statusChangedAt?: string | null;
  statusChangedBy?: {
    id: number;
    username?: string;
    fullName?: string;
    email?: string;
  } | null;
  createdAt: string;
  modalities: Modality[];
  specialties: Specialty[];
  serviceAreas: ServiceArea[];
  contacts?: SpecialistContact[]; // ✅ NUEVO: Contactos del especialista
  coverageArea?: CoverageArea; // ✅ NUEVO: Área de cobertura geográfica
  temporaryAddresses?: TemporaryAddressDTO[]; // ✅ NUEVO: Direcciones temporales (texto libre)
}

export interface SpecialistStatusHistoryEntry {
  id: number;
  specialistId: number;
  previousActive: boolean;
  newActive: boolean;
  reason: string;
  changedAt: string;
  changedBy?: {
    id: number;
    username?: string;
    fullName?: string;
    email?: string;
  } | null;
}

export interface SpecialistStatusHistoryResponse {
  history: SpecialistStatusHistoryEntry[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

export interface Modality {
  modalityId: number;
  code: string;
  name: string;
  notes?: string;
}

export interface Specialty {
  specialtyId: number;
  name: string;
}

export interface ServiceArea {
  serviceId: number;
  name: string;
}

// ✅ NUEVA: Interface para respuesta paginada de especialistas
export interface SpecialistsResponse {
  specialists: Specialist[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// ✅ NUEVA: Interface para estadísticas de especialistas
export interface SpecialistsStats {
  totalSpecialists: number;
  psychologists: number;
  advisors: number;
  others: number;
}

// ✅ ACTUALIZADA: Interface para respuesta de API
export interface PhoneCallResponse {
  success: boolean;
  message: string;
  data?: PhoneCall;
  phoneCallId?: number;
  employee?: Employee;
}

// Interface para estadísticas del dashboard (conteos por status del agente)
export interface AgentDashboardStats {
  totalCalls: number;
  draft: number;
  captured: number;
  assigned: number;
  inProgress: number;
  evidencePending: number;
  completed: number;
}

export interface PrivacyNotice {
  version: string;
  lastUpdated: string;
  content: string;
}

// ✅ ACTUALIZADA: Interface para empresas (accounts)
export interface Company {
  orgId: string;
  orgPath: string;
  sessionCount: number;
  ciudad?: string;
  activa: boolean;
  createdAt?: string;
  updatedAt?: string;
}

